import { useState } from "react";
import { Icon } from "@iconify/react";
import { cn } from "@/shared/lib/utils";
import type { EmpiRecordGolden } from "@/shared/types/empi";
import { fieldSourceLines, goldenTag, sourceName, trustText } from "./empiPresentation";

/**
 * Module 5 golden records on the patient's clinical pages. A row that stands
 * for a resolved cluster carries `golden`; the list shows one clean line with
 * a single tag ("Confirmed by hospital record" / "Seen in N sources" / "From a
 * document — not yet confirmed"), and where it came from stays one click away
 * (GoldenSources, collapsed by default).
 */

const TONE = {
  confirmed: { icon: "lucide:badge-check", className: "bg-[#ECFDF3] text-[#027A48]" },
  seen: { icon: "lucide:layers", className: "bg-[#EEF2F7] text-[#0A2947]" },
  document: { icon: "lucide:file-question", className: "bg-[#FEF6DC] text-[#946200]" },
} as const;

export const PENDING_CHANGE_LABEL = "Change waiting for review";

/** The one-line trust tag. A plain label (not a button), so it can sit inside clickable rows. */
export function GoldenTag({ golden }: { golden: EmpiRecordGolden | null | undefined }) {
  if (!golden) return null;
  const tag = goldenTag(golden.goldenConfidence, golden.corroboratingSourceCount);
  return (
    <>
      <span data-testid="golden-tag" className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold", TONE[tag.tone].className)}>
        <Icon icon={TONE[tag.tone].icon} className="h-3 w-3" />
        {tag.label}
      </span>
      {golden.pendingChange && (
        // The value shown is the last verified one; a newer upload disagrees and
        // is waiting in the Human Review Queue.
        <span
          data-testid="golden-pending-change"
          title="A newer upload has a different value. It will show here once it's reviewed."
          className="ml-1 inline-flex items-center gap-1 rounded-full bg-[#FFF5F7] px-2 py-0.5 text-[10px] font-bold text-[#E4002B]"
        >
          <Icon icon="lucide:hourglass" className="h-3 w-3" />
          {PENDING_CHANGE_LABEL}
        </span>
      )}
    </>
  );
}

/** "Where this came from": every source, which one is shown, and — for a value
 *  filled in from another source — which source it actually came from.
 *  `compact`: a small grey link, for lists where the record itself is the focus. */
/** Sources listed in full before the list scrolls instead of growing. */
const SOURCES_BEFORE_SCROLL = 10;

/** Optional extra for one source line, keyed by recordId: a detail (e.g. "Pages 37–40")
 *  and an action (e.g. "View original pages"). Lines without one render as before. */
export interface GoldenSourceExtra {
  detail?: string;
  action?: { label: string; onClick: () => void };
}

export function GoldenSources({ golden, className, compact = false, defaultOpen = false, sourceExtras }: {
  golden: EmpiRecordGolden | null | undefined; className?: string; compact?: boolean;
  /** Start expanded — when the user already asked for the details (e.g. a lab result's info icon). */
  defaultOpen?: boolean;
  sourceExtras?: Record<string, GoldenSourceExtra>;
}) {
  const [open, setOpen] = useState(defaultOpen);
  if (!golden) return null;
  const fromOthers = fieldSourceLines(golden.fieldSources, golden.sources);
  return (
    <div className={className} data-testid="golden-sources">
      <button
        type="button"
        aria-expanded={open}
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        className={cn(
          // Its own size and alignment: an unlayered "button { font: inherit }" reset
          // otherwise gave it the surrounding card's font, centred (Care & Treatment).
          "inline-flex items-center gap-1 !text-left font-semibold hover:text-[#E4002B]",
          compact ? "!text-[11px] !text-[#6B7280]" : "!text-xs !text-[#0A2947]",
        )}
      >
        <Icon icon={open ? "lucide:chevron-down" : "lucide:chevron-right"} className="h-3.5 w-3.5" />
        Where this came from ({golden.sources.length} {golden.sources.length === 1 ? "source" : "sources"})
      </button>
      {open && (
        <div className="mt-2 grid gap-2 rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] p-3">
          {/* More than 10 sources: about 10 rows tall with its own scrollbar, so a
              long history never pushes the page; the copy shown comes first. */}
          <ul
            className={cn("!mb-0 grid gap-1.5 !pl-0", golden.sources.length > SOURCES_BEFORE_SCROLL && "max-h-60 overflow-y-auto pr-1")}
            aria-label="Sources"
            data-testid="golden-sources-list"
          >
            {[...golden.sources].sort((a, b) => Number(b.isGolden) - Number(a.isGolden)).map((source) => (
              <li key={source.recordId} data-testid="golden-source" className="flex flex-wrap items-center gap-x-2 text-xs text-[#4B5563]">
                <Icon icon="lucide:file-text" className="h-3 w-3 shrink-0 text-[#9CA3AF]" />
                <span className="font-semibold text-[#0A2947]">{sourceName(source)}</span>
                <span>· {trustText(source)}</span>
                {source.recordedAs && <span data-testid="golden-source-recorded-as">· recorded as "{source.recordedAs}"</span>}
                {source.isGolden && (
                  <span className="rounded-full bg-[#EEF2F7] px-1.5 py-0.5 text-[10px] font-bold text-[#0A2947]">Shown</span>
                )}
                {sourceExtras?.[source.recordId]?.detail && (
                  <span data-testid="golden-source-detail">· {sourceExtras[source.recordId].detail}</span>
                )}
                {sourceExtras?.[source.recordId]?.action && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); sourceExtras[source.recordId].action?.onClick(); }}
                    className="text-xs font-bold text-[#E4002B]/80 hover:text-[#E4002B]"
                  >
                    {sourceExtras[source.recordId].action?.label}
                  </button>
                )}
              </li>
            ))}
          </ul>
          {fromOthers.length > 0 && (
            <div data-testid="golden-field-sources" className="border-t border-[#E5E7EB] pt-2">
              <p className="text-[11px] font-semibold text-[#6B7280]">Filled in from another source:</p>
              <ul className="!mb-0 mt-1 grid gap-1 !pl-0">
                {fromOthers.map((line) => (
                  <li key={line.field} className="text-xs text-[#4B5563]">
                    <span className="font-semibold text-[#0A2947]">{line.field}</span> (from {line.source})
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

